import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AnomalyIncidentsResponse,
  AnomalyMetricKey,
  AnomalyMetricSemantics,
  getEmptyAnomalyIncidentsResponse,
} from '../../../models/anomaly-detection/models/AnomalyIncidentsResponse';
import { getAnomalyIncidents } from '../../../services/anomaly-detection/AnomalyIncidentService';
import { GET_ANOMALY_INCIDENTS } from '../../query/anomaly-detection/keys';

const emptyIncidents = getEmptyAnomalyIncidentsResponse();

export const useGetAnomalyIncidents = (
  projectId: number | null,
  from: Date | null,
  to: Date | null,
  keywordsSet: Set<string>,
  filterChatTypes?: string[],
  enabled: boolean = true,
) => {
  const queryClient = useQueryClient();

  const keywords = Array.from(keywordsSet);

  const queryEnabled = enabled && !!projectId && !!from && !!to && keywords.length > 0;

  const queryKey = [GET_ANOMALY_INCIDENTS, projectId, from, to, keywords, filterChatTypes];

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<AnomalyIncidentsResponse>({
    queryKey,
    queryFn: () => getAnomalyIncidents(projectId!, from, to, keywords, filterChatTypes),
    staleTime: 1000 * 60 * 60 * 6,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: queryEnabled,
  });

  const incidentsByMetric = queryEnabled && data ? data : emptyIncidents;

  const metricSemantics = Object.fromEntries(
    (Object.keys(incidentsByMetric) as AnomalyMetricKey[]).map((metric) => [
      metric,
      incidentsByMetric[metric].semantics,
    ]),
  ) as Record<AnomalyMetricKey, AnomalyMetricSemantics>;

  return {
    brandScoreAnomalies: incidentsByMetric.brand_score.data,
    averagePositionAnomalies: incidentsByMetric.average_position.data,
    visibilityAnomalies: incidentsByMetric.visibility.data,
    metricSemantics,
    isLoading: queryEnabled ? isLoading : false,
    isError: queryEnabled ? isError : false,
    error: queryEnabled ? error : null,
    refetch,
    invalidate: () =>
      queryClient.invalidateQueries({
        queryKey,
        refetchType: 'active',
      }),
  };
};
